
import { Component, Prop, Vue } from "vue-property-decorator";

import Card from "@/components/card/ResourceCards.vue";
import CollapsibleDescription from "@/components/common/CollapsibleDescription.vue";
import CardBodyItem from "@/components/card/CardBodyItem.vue";
import CardBodyItemSkill from "@/components/card/CardBodyItemSkill.vue";
import LocationIconNew from "@/components/icons/LocationIconNew.vue";
import UserIcon from "@/components/icons/user.vue";
import CalendarIcon from "@/components/icons/calendar.vue";
import DollarIcon from "@/components/icons/dollar.vue";
import ProfileIcon from "@/components/icons/profile.vue";
import ItemChipsWithMore from "@/components/common/ItemChipsWithMore.vue";
import Badge from "@/components/common/BadgeNew.vue";
import CustomRequestModal from "@/components/requests/CustomRequestModalNew.vue";
import CustomRequestActions from "@/components/requests/CustomRequestActionsNew.vue";

import { getStatusName } from "@/helpers/requests/getStatus";
import getFullName from "@/helpers/getFullName";
import { NO_INFORMATION } from "@/data/placeholders";
import {
  Custom_Request,
  Custom_Request_Skills,
  Skill,
  User,
  Tag,
} from "@/generated/graphql";
import RouteNames from "@/entities/RouteNames";
import RouteParams from "@/entities/RouteParams";
import { CustomRequestStatus } from "@/entities/CustomRequestStatus";
import BadgeColor from "@/entities/BadgeColor";
import { Moment } from 'moment';
import { customRequestSkillsNumber } from "@/data/skillChipsNumber";
import CollapsibleDescriptionLength from "@/entities/CollapsibleDescriptionLength";
import SuitableResources from "@/components/requests/SuitableResourcesNew.vue";
import S3Image from "@/components/common/s3-image.vue";
import ChipsWithMore from "@/components/common/ChipsWithMore.vue";
import ChipWithLinkData from "@/types/ChipWithLinkData";
import { hslColorFromName } from "@/utils/hslColorFromName";

@Component({
  components: {
    SuitableResources,
    Card,
    CollapsibleDescription,
    LocationIconNew,
    CalendarIcon,
    DollarIcon,
    ProfileIcon,
    UserIcon,
    CardBodyItem,
    CardBodyItemSkill,
    S3Image,
    ItemChipsWithMore,
    Badge,
    CustomRequestModal,
    CustomRequestActions,
    ChipsWithMore,
  },
})
export default class CustomRequestCard extends Vue {
  @Prop({ type: Array, default: () => [] })
  readonly skills!: Custom_Request_Skills[];
  @Prop({ type: Object, required: true }) readonly request!: Custom_Request;
  @Prop({ type: Object, required: true }) readonly createdBy!: User;
  @Prop({ type: Boolean, required: true }) readonly isAdmin!: boolean;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: String, default: "h-6 w-6" }) readonly avatarClasses!: string;
  @Prop({ type: Boolean, default: false }) readonly highlightAvatar!: boolean;

  modalShown = false;
  collapsibleDescriptionLength = CollapsibleDescriptionLength;
  skillsNumber = customRequestSkillsNumber;

  get skillIds(): number[] {
    return this.requestSkills.map(({ id }: Skill) => id);
  }

  get excludeResourceUserId(): string {
    return this.request?.project_activity?.contractor?.user_id;
  }
  get Rate(): string {
    if (this.request?.min_budget && this.request?.min_budget !== null) {
      return `$ ${this.request?.min_budget} - $ ${this.request?.max_budget}`;
    } else {
      return NO_INFORMATION;
    }
  }
  get requestJobTitle(): string {
    return this.request?.job_title || NO_INFORMATION;
  }
  get requestTagname(): Tag {
    //@ts-ignore
    return this.request?.tag?.name || NO_INFORMATION;
  }

  get requestDescription(): string {
    return this.request?.description || NO_INFORMATION;
  }
  message(): void {
    // var recipientId = null;
    // var currentUser:User = Vue.prototype.$currentUser;
    // if(currentUser.role === 'ADMIN') {
    //   recipientId = this.createdBy.id;
    // } else {
    //   recipientId = this.request?.company?.admins[0]?.user.id;
    // }
    // this.$router.push({
    //   name: RouteNames.INBOX,
    //   query: {
    //     recipientUserId: recipientId,
    //   },
    // });
    this.$router.push({
      name: RouteNames.REQUEST_CONVERSATIONS,
      params: {
        id: this.request?.id,
      },
    });
  }

  get badgeColor(): string {
    switch (this.request?.status) {
      case CustomRequestStatus.UNDER_REVIEW:
        return BadgeColor.INFO;
      case CustomRequestStatus.APPLICANTS_SENT:
        return BadgeColor.WARNING;
      default:
        return BadgeColor.SUCCESS;
    }
  }

  get requestSkills(): Skill[] {
    return this.skills.map(({ skill }) => skill);
  }

  get requestSkillsTalent(): any[] {
    if (this.skills) {
      return this.skills.reduce((acc: any[], { skill }) => {
        if (skill.name) {
          acc.push({ name: skill.name, id: skill.id });
        }
        return acc;
      }, []);
    }
    return [];
  }

  get requestCreatorName(): string {
    const createdBy = this.request?.created_by;
    return getFullName(createdBy.given_name!, createdBy.family_name!);
  }
  get requestTagName(): Tag {
    const createdBy = this.request?.tag_id;
    return createdBy.name;
  }
  get createdByCurrentUser(): boolean {
    return this.request?.created_by.id === this.currentUserId;
  }

  get requestCreatorLink(): RouteParams<{ id: string }> | undefined {
    const managerId = this.request?.created_by.manager?.id;
    if (!managerId) {
      return undefined;
    }
    return {
      name: RouteNames.MANAGER_ABOUT,
      params: { id: managerId },
    };
  }

  // get requestCLink(): RouteParams<{ id: string }> | undefined {
  //   const managerId = this.request?.created_by.manager?.id;
  //   if (!managerId) {
  //     return undefined;
  //   }
  //   return {
  //     name: (RouteNames.REQUESTS ),
  //     params: { id: managerId }
  //   }
  // }

  get requestLink(): RouteParams<{ id: string }> | undefined {
    const requestId = this.request?.id;
    if (!requestId) {
      return undefined;
    }
    return {
      name: RouteNames.CUSTOM_REQUESTS_RESOURCES_SUGGESTED,
      params: { id: requestId },
    };
  }

  get status(): string {
    return getStatusName(this.request?.status);
  }

  toggleRequestModal(): void {
    this.modalShown = !this.modalShown;
  }

  handleRefetch(): void {
    this.$emit("refetch");
  }

  transformDate(dateToChange: string): string {
    if (dateToChange) {
      const joinedDate = new Date(dateToChange);
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      const year = joinedDate.getFullYear();
      const month = joinedDate.getMonth();

      const formattedDate = `${monthNames[month]} ${year}`;

      return formattedDate;
    }
    return NO_INFORMATION;
  }

  startDate(date): string {
		const startDate = this.$moment(
			new Date(date)
		) as unknown as Moment;
		return startDate.format('DD MMM, YYYY');
	}

	endDate(date): string {
		const endDate = this.$moment(
			new Date(date)
		) as unknown as Moment;
		return endDate.format('DD MMM, YYYY');
	}


  get defaultInitialsClasses(): string {
    return "inline-flex h-6 w-6 rounded-full items-center justify-center";
  }
  

  get initial(): string {
    const firstName = this.request?.created_by?.given_name;
    const lastName = this.request?.created_by?.family_name;

    if (!firstName && !lastName) {
      return ''
    }

    const givenName = firstName ? firstName.trimLeft()[0] : '';
    const familyName = lastName ? lastName.trimLeft()[0] : '';
    return `${givenName}${familyName}`;
  }

  get bgStyle(): object {
    return {
        background: hslColorFromName(this.initial || ""),
    };
  }
  
  get avatarStyleClasses(): Record<string, boolean> {
    return {
      [this.avatarClasses]: Boolean(this.avatarClasses),
      ["border border-primary"]: this.highlightAvatar,
    };
  }
  get defaultImageClasses(): string {
    return "inline-block rounded-full object-cover object-center";
  }
}
